import {
  PLATFORM_LIGHT_THEME,
  PLATFORM_DARK_THEME,
} from './generated/platform-theme'

export interface TypographyToken {
  size: string | number
  weight: string | number
  lineHeight: string | number
  family?: string
}

export interface DesignTokens {
  colors: Record<string, string>
  spacing: Record<string, string | number>
  typography: Record<string, TypographyToken>
  radius: Record<string, string | number> | string
  shadow: Record<string, string>
  fontFamily?: string
  fontWeightDefault?: number
  /**
   * Phase 393 WR-03: per-slot font stacks aligned with ThemeTokens.fonts.
   * Populated by lookupPreset → ThemeTokens.fonts and consumed by
   * applyTokensToCSSVars to emit --font-sans / --font-mono / --font-heading.
   */
  fonts?: { sans?: string; mono?: string; heading?: string }
  resolve(path: string): unknown
}

interface RawDesignTokens {
  colors: Record<string, string>
  spacing: Record<string, string | number>
  typography: Record<string, TypographyToken>
  radius: Record<string, string | number> | string
  shadow: Record<string, string>
  fontFamily?: string
  fontWeightDefault?: number
  fonts?: { sans?: string; mono?: string; heading?: string }
}

export function loadDesignTokens(raw: RawDesignTokens): DesignTokens {
  return {
    ...raw,
    resolve(path: string): unknown {
      const keys = path.split('.')
      let current: unknown = raw
      for (const key of keys) {
        if (current == null || typeof current !== 'object') return undefined
        current = (current as Record<string, unknown>)[key]
      }
      return current
    },
  }
}

export function mergeTokens(
  base: RawDesignTokens,
  override: Partial<RawDesignTokens>,
): RawDesignTokens {
  return {
    colors: { ...base.colors, ...override.colors },
    spacing: { ...base.spacing, ...override.spacing },
    typography: { ...base.typography, ...override.typography },
    radius: override.radius !== undefined ? override.radius : base.radius,
    shadow: { ...base.shadow, ...override.shadow },
    fontFamily: override.fontFamily ?? base.fontFamily,
    fontWeightDefault: override.fontWeightDefault ?? base.fontWeightDefault,
    fonts:
      override.fonts || base.fonts
        ? { ...base.fonts, ...override.fonts }
        : undefined,
  }
}

// ---- Default theme constants (shadcn/ui) ----
//
// The platform's ONE token set (operator decision, 2026-08-25:
// "플랫폼 tokenRef 는 1개 되어야 한다"). PLATFORM_LIGHT_THEME /
// PLATFORM_DARK_THEME are GENERATED from
// packages/cogo-design-presets/themes/default-theme.json — the canonical
// ThemeV2 instance the DS panel previews and themes-v1.ttl names. Until
// 2026-08-25 the values below were hand-authored and bound to nothing: 17 of
// the 23 slots they shared with that theme disagreed, `primary` among them.
// Edit the canonical theme (or the axes its generator reads) and re-run
// scripts/generate-platform-theme.ts — never edit the generated file.
//
// Complete set of CSS variable values so app content is fully isolated from
// the Studio shell theme: getIsolatedAppStyle writes every entry here onto
// the app's render root BEFORE any app token is read, which is what makes
// "the app wins per slot, the platform holds the rest" decidable rather than
// contingent on what a payload happens to declare.

/**
 * Colour slots this renderer emits that the canonical platform theme does not
 * declare.
 *
 * shadcn v4 dropped `--destructive-foreground`, but product code still writes
 * `text-destructive-foreground` (studio publishing, document workspace,
 * appliance admin, apps-page, page-editor's button …), so dropping the
 * variable would leave those elements with no foreground colour. It keeps its
 * pre-existing value: the canonical theme has no opinion for it to inherit.
 *
 * platform-theme-parity.test.ts fails on any extra slot not recorded both
 * here and in its DECLARED_EXTRA_SLOTS, so a second one cannot arrive quietly.
 */
const RENDERER_ONLY_SLOTS: Record<string, string> = {
  'destructive-foreground': 'oklch(0.984 0.003 247.858)',
}

export const DEFAULT_LIGHT_THEME: Record<string, string> = {
  ...PLATFORM_LIGHT_THEME,
  ...RENDERER_ONLY_SLOTS,
}

export const DEFAULT_DARK_THEME: Record<string, string> = {
  ...PLATFORM_DARK_THEME,
  ...RENDERER_ONLY_SLOTS,
}

// ---- CSS custom property injection ----

/**
 * Map from token camelCase color key → CSS variable name (without --).
 *
 * Exported (as {@link COLOR_TOKEN_KEY_TO_CSS_VAR}) because it is the ONLY
 * definition of "which names in a token record become colour CSS variables".
 * Callers that must recognise a colour slot before handing the record to
 * `getIsolatedAppStyle` have to derive that vocabulary from HERE rather than
 * restate it, or the two lists drift and the caller silently drops slots this
 * writer would have emitted.
 */
const COLOR_KEY_TO_CSS: Record<string, string> = {
  background: 'background',
  foreground: 'foreground',
  card: 'card',
  cardForeground: 'card-foreground',
  popover: 'popover',
  popoverForeground: 'popover-foreground',
  primary: 'primary',
  primaryForeground: 'primary-foreground',
  secondary: 'secondary',
  secondaryForeground: 'secondary-foreground',
  muted: 'muted',
  mutedForeground: 'muted-foreground',
  accent: 'accent',
  accentForeground: 'accent-foreground',
  destructive: 'destructive',
  destructiveForeground: 'destructive-foreground',
  border: 'border',
  input: 'input',
  ring: 'ring',
  // Status tokens
  success: 'success',
  successForeground: 'success-foreground',
  warning: 'warning',
  warningForeground: 'warning-foreground',
  info: 'info',
  infoForeground: 'info-foreground',
  // Chart tokens (shadcn charts)
  chart1: 'chart-1',
  chart2: 'chart-2',
  chart3: 'chart-3',
  chart4: 'chart-4',
  chart5: 'chart-5',
  // Sidebar tokens (shadcn sidebar)
  //
  // TWO names, deliberately both present. shadcn v4 calls the surface itself
  // `--sidebar`, and that is the name the platform theme declares and the
  // name 14 files read as `var(--sidebar)`. `--sidebar-background` is the
  // older spelling, still defined locally by cogo-page-editor and
  // cogo-actionflow-editor in their own index.css. No app payload in the repo
  // declares `sidebarBackground`; 19 declare `sidebar`.
  //
  // Without the `sidebar` row an app that declared its sidebar colour kept
  // the PLATFORM colour on `--sidebar` — a declared app value overridden by
  // the platform, which platform-app-token-isolation.test.ts now forbids.
  sidebar: 'sidebar',
  sidebarBackground: 'sidebar-background',
  sidebarForeground: 'sidebar-foreground',
  sidebarPrimary: 'sidebar-primary',
  sidebarPrimaryForeground: 'sidebar-primary-foreground',
  sidebarAccent: 'sidebar-accent',
  sidebarAccentForeground: 'sidebar-accent-foreground',
  sidebarBorder: 'sidebar-border',
  sidebarRing: 'sidebar-ring',
}

/**
 * The colour-slot table above, published for callers that must decide whether a
 * name in a token record is a colour slot. Same object — not a copy — so a slot
 * added to the writer is visible to those callers in the same commit.
 */
export const COLOR_TOKEN_KEY_TO_CSS_VAR: Readonly<Record<string, string>> =
  COLOR_KEY_TO_CSS

// ---- Pass-through CSS variables (non-slot names) ----

/**
 * A CSS custom property name this module will emit VERBATIM.
 *
 * `--` prefix required, then at least one ASCII letter / digit / `_` / `-`.
 * A bare name (`fg`) is rejected rather than auto-prefixed: prefixing would be
 * this module inventing the app's name, and invention is the one thing the
 * pass-through channel exists to avoid.
 *
 * Deliberately narrower than the CSS grammar, which also admits escapes and
 * non-ASCII idents. Everything outside this set is REJECTED, not repaired.
 */
const PASSTHROUGH_CSS_VAR_NAME_RE = /^--[A-Za-z0-9_-]+$/

/**
 * Characters that cannot appear in a single legal declaration value. Their
 * presence means the payload is trying to write more than one declaration —
 * these variables land in an inline `style` attribute, so a `;` would smuggle
 * a second declaration onto the app render root.
 */
const PASSTHROUGH_CSS_VAR_VALUE_REJECT_RE = /[;{}]/

/**
 * Names the app declares for itself that the slot vocabulary
 * ({@link COLOR_TOKEN_KEY_TO_CSS_VAR}) does not know — `--fg`, `--bg`,
 * `--gold`, `--fg-subtle`, … Applications converted from real production
 * source reference their own design system through arbitrary Tailwind values
 * (`bg-[var(--fg)]`), which resolve against exactly these names and nothing
 * else.
 *
 * Values are carried through UNCHANGED: no hex→hsl normalisation (that is the
 * colour channel's job, and it presumes the value means a colour), and no
 * `--color-*` dual-write (`bg-[var(--fg)]` reads `--fg` literally, so writing
 * `--color-fg` would be assigning the name a Tailwind theme-namespace meaning
 * it does not have). Nothing here is mapped onto a slot: a name says only
 * what the app said it says.
 *
 * Rejections are announced, never silent — a dropped declaration is
 * indistinguishable from a declaration nobody made, which is the failure this
 * channel was opened to end.
 */
function collectPassthroughCssVars(
  cssVars: Record<string, string> | undefined,
): Record<string, string> {
  const collected: Record<string, string> = {}
  if (!cssVars || typeof cssVars !== 'object') return collected
  for (const [name, raw] of Object.entries(cssVars)) {
    if (!PASSTHROUGH_CSS_VAR_NAME_RE.test(name)) {
      warnRejectedPassthrough(name, 'not a CSS custom property name')
      continue
    }
    if (typeof raw !== 'string') {
      warnRejectedPassthrough(name, 'value is not a string')
      continue
    }
    const value = raw.trim()
    if (!value) {
      warnRejectedPassthrough(name, 'value is empty')
      continue
    }
    if (PASSTHROUGH_CSS_VAR_VALUE_REJECT_RE.test(value)) {
      warnRejectedPassthrough(name, 'value contains ; { or }')
      continue
    }
    collected[name] = value
  }
  return collected
}

function warnRejectedPassthrough(name: string, reason: string): void {
  console.warn(
    `[cogo] design token cssVars entry dropped (${reason}): ${JSON.stringify(name)}`,
  )
}

/** Check if a string is already in HSL channel format "H S% L%" */
const HSL_CHANNEL_RE = /^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%$/
function isHslChannels(value: string): boolean {
  return HSL_CHANNEL_RE.test(value.trim())
}

/** Check if a string is an oklch() color value */
function isOklch(value: string): boolean {
  return value.trim().startsWith('oklch(')
}

function normalizeTokenColor(raw: string): string | null {
  const value = raw.trim()
  if (!value) return null
  if (
    /^(rgb\(|rgba\(|hsl\(|hsla\(|oklch\(|oklab\(|color\(|var\()/i.test(value)
  ) {
    return value
  }
  const hsl = isHslChannels(value) ? value : hexToHslChannels(value)
  return hsl ? `hsl(${hsl})` : null
}

/** Resolve a color value from tokens, trying both camelCase key and kebab-case CSS var name */
function resolveColorValue(
  colors: Record<string, string>,
  camelKey: string,
  cssVar: string,
): string | undefined {
  return colors[camelKey] ?? colors[cssVar]
}

/** Convert hex (#RRGGBB or #RGB) to HSL channel string "H S% L%" for Tailwind. */
function hexToHslChannels(hex: string): string | null {
  const cleaned = hex.replace('#', '')
  let r: number, g: number, b: number
  if (cleaned.length === 3) {
    r = parseInt(cleaned[0] + cleaned[0], 16) / 255
    g = parseInt(cleaned[1] + cleaned[1], 16) / 255
    b = parseInt(cleaned[2] + cleaned[2], 16) / 255
  } else if (cleaned.length === 6) {
    r = parseInt(cleaned.slice(0, 2), 16) / 255
    g = parseInt(cleaned.slice(2, 4), 16) / 255
    b = parseInt(cleaned.slice(4, 6), 16) / 255
  } else {
    return null
  }

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0,
    s = 0

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
    else if (max === g) h = ((b - r) / d + 2) / 6
    else h = ((r - g) / d + 4) / 6
  }

  const hDeg = Math.round(h * 3600) / 10
  const sPct = Math.round(s * 1000) / 10
  const lPct = Math.round(l * 1000) / 10
  return `${hDeg} ${sPct}% ${lPct}%`
}

/**
 * Build a CSS variable map from design tokens, suitable for element-scoped
 * inline styles. Returns e.g. { '--primary': 'oklch(0.488 0.243 264.376)', ... }
 */
export function getTokenCSSVars(tokens: {
  colors?: Record<string, string>
  radius?: Record<string, string | number> | string
  fonts?: { sans?: string; mono?: string; heading?: string }
  fontFamily?: string
}): Record<string, string> {
  const vars: Record<string, string> = {}

  if (tokens.colors) {
    for (const [key, cssVar] of Object.entries(COLOR_KEY_TO_CSS)) {
      const raw = resolveColorValue(tokens.colors, key, cssVar)
      if (!raw) continue
      const color = normalizeTokenColor(raw)
      if (color) vars[`--${cssVar}`] = color
    }
  }

  if (tokens.radius) {
    if (typeof tokens.radius === 'string') {
      vars['--radius'] = tokens.radius
    } else {
      const radiusLg = tokens.radius.lg ?? tokens.radius.md
      if (radiusLg) vars['--radius'] = String(radiusLg)
    }
  }

  if (tokens.fontFamily) {
    vars['--cogo-font-family'] = tokens.fontFamily
  }

  // Phase 393: emit --font-sans / --font-mono / --font-heading when tokens.fonts
  // is provided. Partial fonts are allowed — only the supplied slots are emitted.
  if (tokens.fonts) {
    if (tokens.fonts.sans) vars['--font-sans'] = tokens.fonts.sans
    if (tokens.fonts.mono) vars['--font-mono'] = tokens.fonts.mono
    if (tokens.fonts.heading) vars['--font-heading'] = tokens.fonts.heading
  }

  return vars
}

/**
 * Build a complete React CSSProperties object for scoping design tokens
 * to a container element. Includes both `--primary` and `--color-primary`
 * so Tailwind v4 utility classes work even when tokens are set on a child
 * element instead of :root.
 */
export function getScopedTokenStyle(tokens: {
  colors?: Record<string, string>
  radius?: Record<string, string | number> | string
  fonts?: { sans?: string; mono?: string; heading?: string }
  fontFamily?: string
  fontWeightDefault?: number
}): Record<string, string> {
  const style: Record<string, string> = {}

  if (tokens.colors) {
    for (const [key, cssVar] of Object.entries(COLOR_KEY_TO_CSS)) {
      const raw = resolveColorValue(tokens.colors, key, cssVar)
      if (!raw) continue
      const color = normalizeTokenColor(raw)
      if (color) {
        style[`--${cssVar}`] = color
        style[`--color-${cssVar}`] = color
      }
    }
  }

  if (tokens.radius) {
    if (typeof tokens.radius === 'string') {
      style['--radius'] = tokens.radius
    } else {
      const radiusLg = tokens.radius.lg ?? tokens.radius.md
      if (radiusLg) style['--radius'] = String(radiusLg)
    }
  }

  if (tokens.fontFamily) {
    style['--cogo-font-family'] = tokens.fontFamily
    style['fontFamily'] = `${tokens.fontFamily}, sans-serif`
  }

  // Phase 393: emit --font-sans / --font-mono / --font-heading when tokens.fonts
  // is provided. Partial fonts are allowed — only the supplied slots are emitted.
  // When both tokens.fonts and the deprecated tokens.fontFamily are provided,
  // tokens.fonts wins for --font-sans (this block runs after the fontFamily block
  // and the new fonts API takes precedence per D-07).
  if (tokens.fonts) {
    if (tokens.fonts.sans) style['--font-sans'] = tokens.fonts.sans
    if (tokens.fonts.mono) style['--font-mono'] = tokens.fonts.mono
    if (tokens.fonts.heading) style['--font-heading'] = tokens.fonts.heading
  }

  if (tokens.fontWeightDefault) {
    style['fontWeight'] = String(tokens.fontWeightDefault)
  }

  return style
}

/**
 * Build a fully-isolated CSS style map for app content areas. Always returns a
 * complete set of CSS variables (never undefined), ensuring the app theme is
 * independent of the host shell (e.g. Studio dark/light toggle).
 *
 * 1. Start with a complete default theme (light or dark)
 * 2. Overlay any project-specific design tokens on top
 * 3. Fill in `tokens.cssVars` — names the slot vocabulary does not know
 * 4. Set `colorScheme` for browser-level isolation (scrollbars, form controls)
 */
export function getIsolatedAppStyle(
  tokens?: {
    colors?: Record<string, string>
    /**
     * Pass-through channel: the app's OWN CSS custom property names, written
     * with their `--` prefix and emitted verbatim — `{ '--fg': '#2A2623' }`.
     *
     * A separate key from `colors` on purpose. `colors` is a SLOT map: its
     * names are the closed vocabulary of {@link COLOR_TOKEN_KEY_TO_CSS_VAR},
     * each carrying a meaning this renderer assigns (and re-assigns per colour
     * scheme). The names here carry no meaning at all — they are whatever the
     * converted application declared for itself. Folding them into `colors`
     * would make one record answer two different questions, which is how a
     * palette got dropped in the first place (#2490).
     *
     * Only `getIsolatedAppStyle` reads it, because only here is the complete
     * slot set guaranteed present, which is what makes the precedence rule
     * below decidable.
     */
    cssVars?: Record<string, string>
    radius?: Record<string, string | number> | string
    fonts?: { sans?: string; mono?: string; heading?: string }
    fontFamily?: string
    fontWeightDefault?: number
  },
  colorScheme: 'light' | 'dark' = 'light',
): Record<string, string> {
  const base = colorScheme === 'dark' ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME

  // Start with complete default theme — every semantic CSS var is defined
  const style: Record<string, string> = {}
  for (const [cssVar, value] of Object.entries(base)) {
    style[`--${cssVar}`] = value
    // oklch values are complete — use directly; legacy HSL channels need wrapping
    style[`--color-${cssVar}`] = isOklch(value) ? value : `hsl(${value})`
  }

  // Default radius — matches shadcn base theme (0.625rem)
  style['--radius'] = '0.625rem'

  // Overlay project tokens (if any)
  if (tokens) {
    const overrides = getScopedTokenStyle(tokens)
    Object.assign(style, overrides)

    // Pass-through fills ONLY names nothing above produced.
    //
    // Precedence: slot-derived wins, always. Every semantic variable is
    // already present by now — the default theme writes the complete set
    // before any project token is read — so a `cssVars` entry naming a slot
    // (`--primary`, `--radius`, `--color-card`, …) is inert by construction.
    // That makes "known slots resolve exactly as they did" unconditional
    // rather than contingent on what a payload happens to declare, and it
    // keeps theme switching the exclusive property of the slot channel: a
    // pass-through name has one value for both colour schemes, so letting one
    // outrank a slot would silently disable dark mode for that name.
    //
    // The consequence is load-bearing for producers: a name that IS a slot
    // must be routed through `colors` (bare, e.g. `card`), never through
    // `cssVars` as `--card`, or the shadcn default outranks the app's value.
    for (const [name, value] of Object.entries(
      collectPassthroughCssVars(tokens.cssVars),
    )) {
      if (Object.prototype.hasOwnProperty.call(style, name)) continue
      style[name] = value
    }
  }

  // Force color-scheme for browser-level isolation
  style['colorScheme'] = colorScheme

  return style
}

/**
 * Apply design tokens to CSS custom properties on a target element.
 * The target element is required — callers must provide an explicit element
 * to scope the tokens to. This prevents accidental CSS variable leaks into
 * the host document (e.g. Studio shell).
 */
export function applyTokensToCSSVars(
  tokens: DesignTokens,
  target: HTMLElement,
): void {
  const root = target

  // Apply color tokens as CSS custom properties
  if (!tokens.colors) return
  for (const [key, cssVar] of Object.entries(COLOR_KEY_TO_CSS)) {
    const raw = resolveColorValue(tokens.colors, key, cssVar)
    if (!raw) continue
    const color = normalizeTokenColor(raw)
    if (color) {
      root.style.setProperty(`--${cssVar}`, color)
      root.style.setProperty(`--color-${cssVar}`, color)
    }
  }

  // Apply border radius
  if (tokens.radius) {
    if (typeof tokens.radius === 'string') {
      root.style.setProperty('--radius', tokens.radius)
    } else {
      const radiusLg = tokens.radius.lg ?? tokens.radius.md
      if (radiusLg) root.style.setProperty('--radius', String(radiusLg))
    }
  }

  // Apply font-family
  if (tokens.fontFamily) {
    root.style.setProperty('--cogo-font-family', tokens.fontFamily)
    root.style.fontFamily = `${tokens.fontFamily}, sans-serif`
  }

  // Phase 393 WR-03: apply per-slot font stacks as --font-sans / --font-mono
  // / --font-heading so callers that build a DesignTokens object with the
  // fonts field get the same CSS variables emitted by getIsolatedAppStyle.
  // Without this branch, applyTokensToCSSVars callers would silently miss
  // the font cascade despite DesignTokens carrying the data.
  if (tokens.fonts) {
    if (tokens.fonts.sans)
      root.style.setProperty('--font-sans', tokens.fonts.sans)
    if (tokens.fonts.mono)
      root.style.setProperty('--font-mono', tokens.fonts.mono)
    if (tokens.fonts.heading)
      root.style.setProperty('--font-heading', tokens.fonts.heading)
  }

  // Apply font-weight default
  if (tokens.fontWeightDefault) {
    root.style.fontWeight = String(tokens.fontWeightDefault)
  }
}
